/**
 * Shared Shuffle Example + Run preview for the teacher sample cards.
 *
 * Used by Model Configuration and the Evaluate New Version confirmation
 * modal. Shuffle loads a random dataset example; Run POSTs ``raw-responses``
 * and fills Model Returns. Infer from response reuses the same Run path when
 * Model Returns is empty. None of these calls persist parser, prompts, or schema.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { sparkToast } from '@spark';
import { extractExtraParamsFromPrompt } from '@/features/datasets/components/DataSelection/ExtraParamsUtils';
import { data_from_server_post } from '@/shared/http/client';
import { useCommercialModels } from '@/shared/hooks/useCommercialModels';
import { usePlaygroundApi } from '@/features/playground/hooks/usePlaygroundApi';
import {
    resolveTeacherSchemaForRequest,
    SCHEMA_ENABLED_EMPTY_MESSAGE,
} from '../utils/supportsJsonSchema';

export const COMMERCIAL_MODEL_REQUIRED_MESSAGE = 'Please select a commercial model.';
export const RUN_PROMPT_REQUIRED_MESSAGE = 'Please enter a prompt.';
export const RUN_MODEL_AND_PROMPT_REQUIRED_MESSAGE =
    'Please select a commercial model and enter a prompt.';
export const BATCH_PREVIEW_NOTICE =
    `This model runs in batches. Results will appear here automatically once the batch job completes. This can take several minutes, depending on the provider queue.`

const BATCH_POLL_INTERVAL_MS = 20000;
const BATCH_POLL_MAX_CONSECUTIVE_FAILURES = 5;

const FALLBACK_CLIENT_ID =
    '40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75';

/**
 * @param {Object} args
 * @param {Object|null} [args.usecase]
 * @param {string|null} [args.teacher] Teacher model id.
 * @param {string} [args.provider]
 * @param {string|null} [args.prompt]
 * @param {string} [args.systemPrompt]
 * @param {string} [args.teacherSchema]
 * @param {boolean} [args.schemaEnabled] True when the JSON schema toggle is on,
 *   even if the textarea is still empty.
 * @param {boolean} [args.autoload] When true, load a dataset sample and teacher
 *   output once the usecase is available (re-eval modal first paint).
 */
export function useTeacherSamplePreview({
    usecase,
    teacher,
    provider,
    prompt,
    systemPrompt,
    teacherSchema,
    schemaEnabled = false,
    autoload = false,
} = {}) {
    const { availableProviders } = useCommercialModels();
    const { schema: schemaToSend, error: schemaError } = resolveTeacherSchemaForRequest({
        schema: teacherSchema,
        provider,
        modelId: teacher,
        availableProviders,
        schemaEnabled,
    });
    const api = usePlaygroundApi();
    const autoloadedForUsecaseRef = useRef(null);
    const [requestExample, setRequestExample] = useState('');
    const [rawResponse, setRawResponse] = useState('');
    const [extraParamsValues, setExtraParamsValues] = useState({});
    const [shuffleLoading, setShuffleLoading] = useState(false);
    const [generatingResponse, setGeneratingResponse] = useState(false);
    const [rawResponseLoading, setRawResponseLoading] = useState(false);
    const [runValidationError, setRunValidationError] = useState(null);
    // True while a batch-only deployment's preview runs as a batch job and
    // we poll ``raw-responses/batch-status`` for its result.
    const [batchPolling, setBatchPolling] = useState(false);

    const batchPollTimerRef = useRef(null);
    // Bumped to invalidate in-flight poll callbacks when polling stops or a
    // new Run supersedes the watched batch job.
    const batchPollSeqRef = useRef(0);

    const stopBatchPolling = useCallback(() => {
        batchPollSeqRef.current += 1;
        if (batchPollTimerRef.current) {
            clearTimeout(batchPollTimerRef.current);
            batchPollTimerRef.current = null;
        }
        setBatchPolling(false);
    }, []);

    useEffect(() => stopBatchPolling, [stopBatchPolling]);

    const startBatchPolling = useCallback((batchIds) => {
        stopBatchPolling();
        const seq = batchPollSeqRef.current;
        setBatchPolling(true);
        let consecutiveFailures = 0;
        const poll = () => {
            data_from_server_post(
                'raw-responses/batch-status',
                { provider, batch_ids: batchIds },
                (result) => {
                    if (batchPollSeqRef.current !== seq) {
                        return;
                    }
                    consecutiveFailures = 0;
                    if (result.pending) {
                        batchPollTimerRef.current = setTimeout(poll, BATCH_POLL_INTERVAL_MS);
                        return;
                    }
                    setBatchPolling(false);
                    if (result.error_message) {
                        sparkToast.error(result.error_message);
                        return;
                    }
                    const value = result.data?.[0]?.value;
                    if (value) {
                        setRawResponse(value);
                    } else {
                        sparkToast.error('Batch job finished without a response.');
                    }
                },
                () => {
                    if (batchPollSeqRef.current !== seq) {
                        return;
                    }
                    consecutiveFailures += 1;
                    if (consecutiveFailures >= BATCH_POLL_MAX_CONSECUTIVE_FAILURES) {
                        setBatchPolling(false);
                        sparkToast.error('Lost track of the batch job status. Run again to retry.');
                        return;
                    }
                    batchPollTimerRef.current = setTimeout(poll, BATCH_POLL_INTERVAL_MS);
                },
            );
        };
        poll();
    }, [provider, stopBatchPolling]);

    useEffect(() => {
        if (!autoload || !usecase?._id || autoloadedForUsecaseRef.current === usecase._id) {
            return undefined;
        }
        autoloadedForUsecaseRef.current = usecase._id;
        setRawResponseLoading(true);
        setRunValidationError(null);

        if (schemaError) {
            setRunValidationError(schemaError);
            setRawResponse('');
            setRawResponseLoading(false);
            return undefined;
        }

        data_from_server_post(
            'raw-responses',
            {
                client_id: usecase.client_id || FALLBACK_CLIENT_ID,
                llm_usecase_id: usecase._id,
                first_index: 0,
                num_records: 1,
                force_generate: true,
                teacher_model: teacher,
                prompt,
                system_prompt: (systemPrompt || '').trim() || null,
                teacher_schema: schemaToSend,
                provider,
            },
            (result) => {
                if (result.error_message) {
                    sparkToast.error(result.error_message);
                    setRawResponseLoading(false);
                    return;
                }
                if (result.batch?.batch_ids?.length) {
                    // Batch-only deployment: the sample runs as a batch job;
                    // poll for its result and fill Model Returns when done.
                    const row = result.data?.[0];
                    setRequestExample(row?.request_example || '');
                    setRawResponseLoading(false);
                    startBatchPolling(result.batch.batch_ids);
                    return;
                }
                if (result.success && result.data && result.data.length > 0) {
                    const row = result.data[0];
                    setRawResponse(row?.value || '');
                    setRequestExample(row?.request_example || '');
                    const extraParams = row?.response?.extra_params || {};
                    const promptKeys = Object.keys(extractExtraParamsFromPrompt(prompt || ''));
                    const initialExtra = {};
                    promptKeys.forEach((key) => {
                        if (extraParams[key] !== undefined) {
                            initialExtra[key] = extraParams[key];
                        }
                    });
                    setExtraParamsValues(initialExtra);
                }
                setRawResponseLoading(false);
            },
            (error_message) => {
                sparkToast.error(error_message);
                setRawResponseLoading(false);
            },
        );
        return undefined;
    }, [
        autoload,
        usecase,
        teacher,
        prompt,
        systemPrompt,
        schemaError,
        schemaToSend,
        provider,
        startBatchPolling,
    ]);

    const handleShuffleExample = useCallback(() => {
        if (!usecase) {
            sparkToast.info('Select a use case to load sample inputs');
            return;
        }
        setShuffleLoading(true);
        api.fetchRandomExample({
            client_id: usecase.client_id,
            llm_usecase_id: usecase._id,
        }, (example) => {
            if (example) {
                setRequestExample(example.value || '');
            } else {
                sparkToast.info('No examples available to shuffle');
            }
            setShuffleLoading(false);
        });
    }, [api, usecase]);

    const generateInFlightRef = useRef(null);

    const generateResponse = useCallback((options) => {
        // Run is wired as onClick={generateResponse}, so ignore click events.
        const forceGenerate = options?.forceGenerate === true;
        // Infer may generate unconstrained when the toggle is on but empty so
        // it can prefill the schema; Run must not.
        const allowEmptyEnabledSchema = options?.allowEmptyEnabledSchema === true;
        const skipEmptyEnabledError = allowEmptyEnabledSchema
            && schemaError === SCHEMA_ENABLED_EMPTY_MESSAGE;
        const hasTeacher = Boolean(teacher);
        const hasPrompt = Boolean((prompt || '').trim());
        if (!hasTeacher || !hasPrompt) {
            if (!hasTeacher && !hasPrompt) {
                setRunValidationError(RUN_MODEL_AND_PROMPT_REQUIRED_MESSAGE);
            } else if (!hasTeacher) {
                setRunValidationError(COMMERCIAL_MODEL_REQUIRED_MESSAGE);
            } else {
                setRunValidationError(RUN_PROMPT_REQUIRED_MESSAGE);
            }
            return Promise.resolve(null);
        }
        if (schemaError && !skipEmptyEnabledError) {
            // Invalid / enabled-empty schema must not run unconstrained — that
            // is how Run used to return a "valid" sample that ignored the field.
            // Drop any previous Model Returns so a blocked Run cannot look like
            // a successful one.
            setRunValidationError(schemaError);
            setRawResponse('');
            return Promise.resolve(null);
        }
        if (!forceGenerate && !requestExample) {
            setRunValidationError('Add a sample input to run.');
            return Promise.resolve(null);
        }

        setRunValidationError(null);
        setGeneratingResponse(true);
        // A new Run supersedes any batch job we were still watching.
        stopBatchPolling();

        const body = {
            client_id: usecase?.client_id || FALLBACK_CLIENT_ID,
            llm_usecase_id: usecase?._id,
            first_index: 0,
            num_records: 1,
            teacher_model: teacher,
            prompt,
            system_prompt: (systemPrompt || '').trim() || null,
            teacher_schema: schemaToSend,
            provider,
        };
        if (forceGenerate) {
            body.force_generate = true;
        } else {
            body.request_example = requestExample;
            body.extra_params = extraParamsValues;
        }

        const request = new Promise((resolve) => {
            data_from_server_post(
                'raw-responses',
                body,
                (result) => {
                    if (result.error_message) {
                        sparkToast.error(result.error_message);
                        setGeneratingResponse(false);
                        resolve(null);
                        return;
                    }
                    if (result.batch?.batch_ids?.length) {
                        // Batch-only deployment: the sample was submitted as
                        // a batch job. Model Returns fills once polling sees
                        // the job complete; resolve(null) because there is no
                        // synchronous output for Infer-style callers.
                        const row = result.data?.[0];
                        if (forceGenerate && row?.request_example) {
                            setRequestExample(row.request_example);
                        }
                        setRawResponse('');
                        setGeneratingResponse(false);
                        startBatchPolling(result.batch.batch_ids);
                        resolve(null);
                        return;
                    }
                    if (result.success && result.data && result.data.length > 0) {
                        const row = result.data[0];
                        const generatedResponse = row?.value;
                        if (generatedResponse) {
                            setRawResponse(generatedResponse);
                            if (forceGenerate && row?.request_example) {
                                setRequestExample(row.request_example);
                            }
                            setGeneratingResponse(false);
                            resolve(generatedResponse);
                            return;
                        }
                        sparkToast.error('Failed to generate response. Please check your API keys.');
                    } else {
                        sparkToast.error('Failed to generate response');
                    }
                    setGeneratingResponse(false);
                    resolve(null);
                },
                (error_message) => {
                    sparkToast.error(error_message);
                    setGeneratingResponse(false);
                    resolve(null);
                },
            );
        });
        generateInFlightRef.current = request;
        request.finally(() => {
            if (generateInFlightRef.current === request) {
                generateInFlightRef.current = null;
            }
        });
        return request;
    }, [
        extraParamsValues,
        prompt,
        provider,
        requestExample,
        schemaError,
        schemaToSend,
        startBatchPolling,
        stopBatchPolling,
        systemPrompt,
        teacher,
        usecase,
    ]);

    const ensureTeacherResponse = useCallback(async () => {
        const existing = (rawResponse || '').trim();
        if (existing) {
            return existing;
        }
        if (generateInFlightRef.current) {
            return generateInFlightRef.current;
        }
        if (batchPolling) {
            // A batch job is already producing this sample; re-running would
            // abandon it and submit another. There is no synchronous output.
            return null;
        }
        if (requestExample) {
            return generateResponse({ allowEmptyEnabledSchema: true });
        }
        return generateResponse({ forceGenerate: true, allowEmptyEnabledSchema: true });
    }, [batchPolling, generateResponse, rawResponse, requestExample]);

    return {
        requestExample,
        setRequestExample,
        rawResponse,
        setRawResponse,
        extraParamsValues,
        setExtraParamsValues,
        shuffleLoading,
        generatingResponse,
        rawResponseLoading,
        runValidationError,
        setRunValidationError,
        handleShuffleExample,
        generateResponse,
        ensureTeacherResponse,
        batchNotice: batchPolling ? BATCH_PREVIEW_NOTICE : null,
    };
}
